<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div class="report-wrapper">

      <!-- Sticky Filter Header -->
      <div class="table-header-filters">
        <v-row dense class="align-center">
          <!-- Date From -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <v-menu v-model="reportFromMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  outlined
                  dense
                  hide-details
                  :value="formatDateFrom"
                  label="Od datuma"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  class="filter-select">
                </v-text-field>
              </template>
              <v-date-picker v-model="dateFrom" no-title scrollable @input="reportFromMenu = false"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Date To -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <v-menu v-model="reportToMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  outlined
                  dense
                  hide-details
                  :value="formatDateTo"
                  label="Do datuma"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  class="filter-select">
                </v-text-field>
              </template>
              <v-date-picker v-model="dateTo" no-title scrollable @input="reportToMenu = false"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Location Select -->
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-autocomplete
              outlined
              dense
              hide-details
              label="Prodajno mjesto"
              v-model="location"
              :items="locations"
              item-text="name"
              item-value="id"
              return-object
              class="filter-select">
            </v-autocomplete>
          </v-col>

          <!-- Unpaid Switch -->
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-switch
              v-model="showUnpaid"
              label="Uključi neplaćene račune"
              hide-details
              class="mt-0 pt-0"
              color="#3989C9">
            </v-switch>
          </v-col>

          <!-- Action Cluster -->
          <v-col cols="auto" class="ml-auto d-flex align-center" style="gap: 8px">
            <v-btn :disabled="locationNotSelected" class="search-btn" @click="getData()" small>
              <v-icon small class="mr-1">mdi-magnify</v-icon>
              Pretraži
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-menu offset-y left :close-on-content-click="true">
                  <template v-slot:activator="{ on: menu, attrs }">
                    <v-btn icon class="export-btn" v-bind="attrs" v-on="{ ...tooltip, ...menu }">
                      <v-icon color="#00b3be">mdi-export-variant</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense style="border-radius: 12px; overflow: hidden; padding: 4px; min-width: 140px;">
                    <v-list-item @click="changeWidth()">
                      <v-icon size="18" color="#E53935" class="mr-2">mdi-file-pdf-box</v-icon>
                      <v-list-item-title>PDF</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="generateExcel()">
                      <v-icon size="18" color="#43A047" class="mr-2">mdi-file-excel</v-icon>
                      <v-list-item-title>Excel</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
              <span>Izvoz podataka</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon :to="to" class="close-btn" v-bind="attrs" v-on="on">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Zatvori izvještaj</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </div>

      <!-- Report Content -->
      <div class="report-content-wrapper">
        <div class="d-flex justify-center">
          <div id="table_info" class="report-tables">
            <!-- Company Info Table (Hidden by default, shown for PDF) -->
            <table id="firma" style="display: none; font-family: 'Roboto-Regular'">
              <tbody>
                <tr>
                  <td>{{ company ? $options.filters.capitalize(company.name) : "" }}</td>
                </tr>
                <tr>
                  <td>{{ company ? company.address + " " + company.city : "" }}</td>
                </tr>
                <tr>
                  <td>OIB: {{ company ? company.oib : "" }}</td>
                </tr>
                <tr>
                  <td>Prodajno mjesto: {{ location ? location.name : "" }}</td>
                </tr>
              </tbody>
            </table>

            <!-- Report Title -->
            <div class="report-title-wrapper">
              <table justify="center" style="width: 100%; border: 0" cellspacing="0" id="title">
                <thead>
                  <tr>
                    <th colspan="6">
                      <p class="report-main-title">Rekapitulacija po kupcima</p>
                      <p class="report-subtitle">za razdoblje od {{ formatDateFrom }} do {{ formatDateTo }}</p>
                    </th>
                  </tr>
                </thead>
              </table>
            </div>

            <!-- Buyers Table -->
            <div class="glass-table-wrapper" v-if="showTableData">
              <table justify="center" style="width: 100%" cellspacing="0" id="tableTraffic">
                <thead>
                  <tr :class="['header', 'naslov']">
                    <th>Način plaćanja</th>
                    <th>Kupac</th>
                    <th>Iznos</th>
                  </tr>
                </thead>
                <tbody style="width: 100%">
                  <!-- Cash Buyers -->
                  <tr v-for="(buyer, bidx) in cash.buyers" :key="buyer.name + bidx + 'c'">
                    <td v-if="bidx === 0" :rowspan="cash.buyers ? cash.buyers.length + 1 : 2" style="vertical-align: top; text-align: left">
                      Gotovina
                    </td>
                    <td style="text-align: left">
                      {{ buyer.name && buyer.name !== "" ? buyer.name : "[nema kupca]" }}
                    </td>
                    <td style="text-align: right">
                      {{
                        viewType === "pdf"
                          ? $options.filters.money(buyer.total, 100, currency).replace("€", "")
                          : buyer.total / 100
                      }}
                    </td>
                  </tr>
                  <tr v-if="cash && cash.buyers && cash.buyers.length > 0">
                    <td class="total-row">Ukupno</td>
                    <td class="total-row" style="text-align: right">
                      {{
                        viewType === "pdf"
                          ? $options.filters.money(cash.total, 100, currency).replace("€", "")
                          : cash.total / 100
                      }}
                    </td>
                  </tr>

                  <!-- Card Buyers -->
                  <tr v-for="(buyer, bidx) in card.buyers" :key="buyer.name + bidx + 'ca'">
                    <td v-if="bidx === 0" :rowspan="card.buyers ? card.buyers.length + 1 : 2" style="vertical-align: top; text-align: left">
                      Kartice
                    </td>
                    <td style="text-align: left">
                      {{ buyer.name && buyer.name !== "" ? buyer.name : "[nema kupca]" }}
                    </td>
                    <td style="text-align: right">
                      {{
                        viewType === "pdf"
                          ? $options.filters.money(buyer.total, 100, currency).replace("€", "")
                          : buyer.total / 100
                      }}
                    </td>
                  </tr>
                  <tr v-if="card && card.buyers && card.buyers.length > 0">
                    <td class="total-row">Ukupno</td>
                    <td class="total-row" style="text-align: right">
                      {{
                        viewType === "pdf"
                          ? $options.filters.money(card.total, 100, currency).replace("€", "")
                          : card.total / 100
                      }}
                    </td>
                  </tr>

                  <!-- Other Buyers -->
                  <tr v-for="(buyer, bidx) in other.buyers" :key="buyer.name + bidx + 'o'">
                    <td v-if="bidx === 0" :rowspan="other.buyers ? other.buyers.length + 1 : 2" style="vertical-align: top; text-align: left">
                      Ostalo
                    </td>
                    <td style="text-align: left">
                      {{ buyer.name && buyer.name !== "" ? buyer.name : "[nema kupca]" }}
                    </td>
                    <td style="text-align: right">
                      {{
                        viewType === "pdf"
                          ? $options.filters.money(buyer.total, 100, currency).replace("€", "")
                          : buyer.total / 100
                      }}
                    </td>
                  </tr>
                  <tr v-if="other && other.buyers && other.buyers.length > 0">
                    <td class="total-row">Ukupno</td>
                    <td class="total-row" style="text-align: right">
                      {{
                        viewType === "pdf"
                          ? $options.filters.money(other.total, 100, currency).replace("€", "")
                          : other.total / 100
                      }}
                    </td>
                  </tr>

                  <!-- Transactional Buyers -->
                  <tr v-for="(buyer, bidx) in transactional.buyers" :key="buyer.name + bidx + 't'">
                    <td v-if="bidx === 0" :rowspan="transactional.buyers ? transactional.buyers.length + 1 : 2" style="vertical-align: top; text-align: left">
                      Transakcijski račun
                    </td>
                    <td style="text-align: left">
                      {{ buyer.name && buyer.name !== "" ? buyer.name : "[nema kupca]" }}
                    </td>
                    <td style="text-align: right">
                      {{
                        viewType === "pdf"
                          ? $options.filters.money(buyer.total, 100, currency).replace("€", "")
                          : buyer.total / 100
                      }}
                    </td>
                  </tr>
                  <tr v-if="transactional && transactional.buyers && transactional.buyers.length > 0">
                    <td class="total-row">Ukupno</td>
                    <td class="total-row" style="text-align: right">
                      {{
                        viewType === "pdf"
                          ? $options.filters.money(transactional.total, 100, currency).replace("€", "")
                          : transactional.total / 100
                      }}
                    </td>
                  </tr>

                  <!-- Deposit Refund Buyers -->
                  <tr v-if="depositRefund && depositRefund.buyers && depositRefund.buyers.length > 0" v-for="(buyer, bidx) in depositRefund.buyers" :key="buyer.name + bidx + 'd'">
                    <td v-if="bidx === 0" :rowspan="depositRefund.buyers.length + 1" style="vertical-align: top; text-align: left">
                      Povratna naknada
                    </td>
                    <td style="text-align: left">
                      {{ buyer.name && buyer.name !== "" ? buyer.name : "[nema kupca]" }}
                    </td>
                    <td style="text-align: right">
                      {{
                        viewType === "pdf"
                          ? $options.filters.money(buyer.total, 100, currency).replace("€", "")
                          : buyer.total / 100
                      }}
                    </td>
                  </tr>
                  <tr v-if="depositRefund && depositRefund.buyers && depositRefund.buyers.length > 0">
                    <td class="total-row">Ukupno</td>
                    <td class="total-row" style="text-align: right">
                      {{
                        viewType === "pdf"
                          ? $options.filters.money(depositRefund.total, 100, currency).replace("€", "")
                          : depositRefund.total / 100
                      }}
                    </td>
                  </tr>

                  <!-- Grand Total -->
                  <tr style="text-align: center">
                    <td class="grand-total"></td>
                    <td class="grand-total">Ukupno</td>
                    <td class="grand-total" style="text-align: right">
                      {{
                        viewType === "pdf"
                          ? $options.filters.money(
                              transactional.total + other.total + card.total + cash.total,
                              100,
                              currency
                            ).replace("€", "")
                          : transactional.total / 100 + other.total / 100 + card.total / 100 + cash.total / 100
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </div>

    </div>
  </v-container>
</template>

<script>
import { DAY, clone } from '@/plugins/utils'
import { df, auth } from '@/plugins/firebase'
import state from '@/state'
import 'jspdf-autotable'
import { saveExcel } from '@/modules/all-reports/xlsx/config.js'
import EventBus from '@/plugins/event-bus'
import { initPdf, setTable, setPageNumber } from '@/modules/all-reports/pdf/config.js'
import avansDiscount from '@/mixins/avans'

export default {
  name: 'Reports',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],
  components: {},
  data: function () {
    return {
      dateFrom: new Date(new Date().getTime() - DAY).toISOString().substr(0, 10),
      dateTo: new Date().toISOString().substr(0, 10),
      location: null,
      locations: [],
      allReceipts: [],
      reportFromMenu: undefined,
      reportToMenu: undefined,
      company: undefined,
      currency: 'EUR',
      payments: {
        cashPayments: 0,
        cardPayments: 0,
        otherPayments: 0,
        transPayments: 0,
        totalPayments: 0,
        pdv: [],
        pdvTotal: { base: 0, rate: 'Ukupno', total: 0 }
      },
      tableWidth: '1200px',
      a4PortraitWidth: '575px',
      a4LandscapeWidth: '610px',
      targetModifier: '',
      viewType: 'pdf',
      showTableData: false,
      cash: { buyers: [{ name: '', total: 0 }], total: 0 },
      card: { buyers: [{ name: '', total: 0 }], total: 0 },
      other: { buyers: [{ name: '', total: 0 }], total: 0 },
      transactional: { buyers: [{ name: '', total: 0 }], total: 0 },
      depositRefund: { buyers: [{ name: '', total: 0 }], total: 0 },
      showUnpaid: false,
      margin: '0px'
    }
  },
  computed: {
    to () {
      return { name: `${this.targetModifier}.all-reports.reportMenu` }
    },
    formatDateTo () {
      return new Intl.DateTimeFormat('hr-HR', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }).format(new Date(this.dateTo))
    },
    formatDateFrom () {
      return new Intl.DateTimeFormat('hr-HR', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }).format(new Date(this.dateFrom))
    },
    locationNotSelected () {
      return !this.location || !this.location.id
    }
  },
  async created () {
    this.margin = state.getMargin()
    await this.getPointsOfSale()
  },
  mounted () {
    var sideNav = this.$route.meta.sideNav
    switch (sideNav) {
      case 'Companies':
        break
      case 'Company':
        this.targetModifier = 'company'
        break
      case 'PointOfSale':
        this.targetModifier = 'pointOfSale'
        break
      case 'CashRegister':
        this.targetModifier = 'pointOfSale'
        break
    }

    EventBus.$on('set-mini', () => {
      this.margin = state.getMargin()
    })

    this.company = clone(state.getCurrentCompany())
    this.location = clone(state.getPointOfSale())

    if (this.company.type === 'pausalni_obrt') {
      this.showUnpaid = false
    } else {
      this.showUnpaid = true
    }
  },
  methods: {
    getPdvDiscounted (receipt, reprezentacija) {
      var tempPdv = []
      if (receipt.pdv_discounted && receipt.pdv_discounted.length > 0) {
        if (reprezentacija === true) {
          var b = 160
          var t = 200
          var tx = 40

          if (receipt.issue_date_time > 1672531210) {
            b = 40
            t = 50
            tx = 10
          }

          if (receipt.type.flag === 'STORNO') {
            b *= -1
            t *= -1
            tx *= -1
          }

          tempPdv.push({
            rate: (2500),
            base: (b),
            total: (t),
            tax: (tx),
            count: 1,
            paymentMethod: receipt.payment_method
          })
        } else {
          receipt.pdv_discounted.forEach(pdv => {
            tempPdv.push({
              rate: (pdv.rate),
              base: (pdv.base),
              total: (pdv.total + pdv.base),
              tax: (pdv.total),
              count: 1,
              paymentMethod: receipt.payment_method
            })
          })
        }
      }

      return tempPdv
    },
    getPdvAndRemoveDiscountIfExist (receipt, reprezentacija) {
      var tempPdv = []
      if (receipt.pdv && receipt.pdv.length > 0) {
        if (reprezentacija === true) {
          var b = 160
          var t = 200
          var tx = 40

          if (receipt.issue_date_time > 1672531210) {
            b = 40
            t = 50
            tx = 10
          }

          if (receipt.type.flag === 'STORNO') {
            b *= -1
            t *= -1
            tx *= -1
          }

          tempPdv.push({
            rate: (2500),
            base: (b),
            total: (t),
            tax: (tx),
            count: 1,
            paymentMethod: receipt.payment_method
          })
        } else {
          receipt.pdv.forEach(pdv => {
            tempPdv.push({
              rate: (pdv.rate),
              base: (pdv.base),
              total: (pdv.total + pdv.base),
              tax: (pdv.total),
              count: 1,
              paymentMethod: receipt.payment_method
            })
          })
        }
      }

      if (tempPdv && tempPdv.length > 0) {
        var d = clone(receipt.discount)
        if (d && d.active === true && d.type !== '' && reprezentacija === false) {
          let totalRemove = d.amount / 100

          tempPdv.sort((a, b) => b.rate - a.rate)

          if (d.name === 'AVANS-STORNO-RECEIPT') {
            if (receipt.type.flag !== 'STORNO') {
              tempPdv = avansDiscount(tempPdv, receipt.tags)
            }
          } else {
            tempPdv.forEach((p, pkey) => {
              if (p.base > 0 && p.total > 0) {
                let totalPrice = p.total / 100

                if (totalPrice >= (1 + (p.rate / 10000))) {
                  let removedAllPossible = false
                  let remove = totalRemove

                  for (var j = totalRemove; j > 0 && removedAllPossible === false; j--) {
                    if ((totalPrice - j) >= (1 + (p.rate / 10000))) {
                      removedAllPossible = true
                      remove = j
                    }
                  }

                  if (removedAllPossible === true) {
                    totalPrice -= remove
                    totalRemove -= remove
                  }
                }

                tempPdv[pkey].total = totalPrice * 100
                tempPdv[pkey].base = parseInt(parseFloat(tempPdv[pkey].total) / (1 + parseFloat(tempPdv[pkey].rate) / 10000))
                tempPdv[pkey].tax = (tempPdv[pkey].total) - (tempPdv[pkey].base)
              }
            })
          }
        }
      }

      return tempPdv
    },
    async getData () {
      this.showLoader()
      const dateFrom = new Date(this.dateFrom).getTime() / 1000
      const dateTo = new Date(this.dateTo).getTime() / 1000

      this.allReceipts = []

      let query = df.collection(`receipts/${state.getCurrentCompany().id}/receipts`)
        .where('issue_date_time', '>=', dateFrom)
        .where('issue_date_time', '<=', dateTo + 86399)
      if (this.location && this.location.id !== 'all') {
        query = query.where('location_details.id', '==', this.location.id)
      }

      await query.get()
        .then((documentSnapshots) => {
          if (documentSnapshots.docs && !documentSnapshots.docs.empty) {
            documentSnapshots.docs.forEach((item) => {
              var rcpt = item.data()
              if (this.showUnpaid === false) {
                if (rcpt.payment && (rcpt.payment.current_status === 'PAID' || rcpt.payment.current_status === 'REVERSED')) {
                  this.allReceipts.push(item.data())
                }
              } else {
                this.allReceipts.push(item.data())
              }
            })
          }
        })

      this.allReceipts.sort((a, b) => a.issue_date_time - b.issue_date_time)

      var cash = {
        buyers: [],
        total: 0
      }

      var card = {
        buyers: [],
        total: 0
      }

      var other = {
        buyers: [],
        total: 0
      }

      var transactional = {
        buyers: [],
        total: 0
      }

      var depositRefund = {
        buyers: [],
        total: 0
      }

      // Process each receipt using PDV data with discount logic
      this.allReceipts.forEach(receipt => {
        if (!receipt.type.payer_name) {
          receipt.type.payer_name = ''
        }

        var reprezentacija = false
        if (receipt.tags) {
          receipt.tags.forEach(tag => {
            if (tag.receipt_type && tag.receipt_type === 'REPREZENTACIJA') {
              reprezentacija = true
            }
          })
        }

        var tempPdv = []

        if (receipt.pdv_discounted && receipt.pdv_discounted.length > 0) {
          tempPdv = this.getPdvDiscounted(receipt, reprezentacija)
        } else if (receipt.pdv && receipt.pdv.length > 0) {
          tempPdv = this.getPdvAndRemoveDiscountIfExist(receipt, reprezentacija)
        }

        // Calculate totals from PDV data (with discount logic applied)
        tempPdv.forEach(pdv => {
          const total = Math.round(pdv.total * 100) / 100

          if (receipt.payment_method === 'NOVČANICE') {
            const idx = cash.buyers.findIndex(p => p?.name === receipt.type.payer_name)
            if (idx > -1) {
              cash.total += total
              cash.buyers[idx].total += total
            } else {
              cash.total += total
              cash.buyers.push({
                name: receipt.type.payer_name,
                total: total
              })
            }
          }

          if (receipt.payment_method === 'KARTICA') {
            const idx = card.buyers.findIndex(p => p?.name === receipt.type.payer_name)
            if (idx > -1) {
              card.total += total
              card.buyers[idx].total += total
            } else {
              card.total += total
              card.buyers.push({
                name: receipt.type.payer_name,
                total: total
              })
            }
          }

          if (receipt.payment_method === 'OSTALO') {
            const idx = other.buyers.findIndex(p => p?.name === receipt.type.payer_name)
            if (idx > -1) {
              other.total += total
              other.buyers[idx].total += total
            } else {
              other.total += total
              other.buyers.push({
                name: receipt.type.payer_name,
                total: total
              })
            }
          }

          if (receipt.payment_method === 'TRANSAKCIJSKI RAČUN') {
            const idx = transactional.buyers.findIndex(p => p?.name === receipt.type.payer_name)
            if (idx > -1) {
              transactional.total += total
              transactional.buyers[idx].total += total
            } else {
              transactional.total += total
              transactional.buyers.push({
                name: receipt.type.payer_name,
                total: total
              })
            }
          }
        })

        // Dodaj PNP u način plaćanja (pdv_discounted ne sadrži PNP iznos)
        if (receipt.pnp && receipt.pnp.total) {
          const pnp = receipt.pnp.total
          const payerName = receipt.type.payer_name
          let bucket = null
          if (receipt.payment_method === 'NOVČANICE') bucket = cash
          else if (receipt.payment_method === 'KARTICA') bucket = card
          else if (receipt.payment_method === 'OSTALO') bucket = other
          else if (receipt.payment_method === 'TRANSAKCIJSKI RAČUN') bucket = transactional
          if (bucket) {
            const idx = bucket.buyers.findIndex(p => p?.name === payerName)
            if (idx > -1) {
              bucket.total += pnp
              bucket.buyers[idx].total += pnp
            } else {
              bucket.total += pnp
              bucket.buyers.push({ name: payerName, total: pnp })
            }
          }
        }

        // Sum deposit refund from this receipt
        if (receipt.naknade && receipt.naknade.povratna && receipt.naknade.povratna.total_amount) {
          const depositTotal = receipt.naknade.povratna.total_amount
          const idx = depositRefund.buyers.findIndex(p => p?.name === receipt.type.payer_name)
          if (idx > -1) {
            depositRefund.total += depositTotal
            depositRefund.buyers[idx].total += depositTotal
          } else {
            depositRefund.total += depositTotal
            depositRefund.buyers.push({
              name: receipt.type.payer_name,
              total: depositTotal
            })
          }
        }
      })

      this.cash = cash
      this.card = card
      this.other = other
      this.transactional = transactional
      this.depositRefund = depositRefund
      this.showTableData = true
      this.hideLoader()
    },
    changeWidth () {
      this.showLoader('', { opacity: 1 })
      const tableInfo = document.getElementById('table_info')
      tableInfo.style.width = this.a4PortraitWidth
      tableInfo.style.maxWidth = this.a4PortraitWidth
      tableInfo.style.fontSize = '80%'

      this.generatePdf()
    },
    async generatePdf () {
      const that = this
      const tableInfo = document.getElementById('table_info')
      try {
        var doc = await initPdf()

        doc = await setTable(doc, '#firma', 10)
        var finalY = doc.lastAutoTable.finalY || 10
        doc = await setTable(doc, '#title', finalY)
        finalY = doc.lastAutoTable.finalY || 10
        doc = await setTable(doc, '#tableTraffic', finalY)

        doc = await setPageNumber(doc)

        doc.save('Rekapitulacija po kupcima.pdf')
      } catch (error) {
        console.error('PDF generation error:', error)
      } finally {
        tableInfo.style.width = '100%'
        tableInfo.style.maxWidth = that.tableWidth
        tableInfo.style.fontSize = '100%'
        this.viewType = 'pdf'
        that.hideLoader()
      }
    },
    generateExcel () {
      this.showLoader('', { opacity: 1 })
      this.viewType = 'excel'
      setTimeout(() => {
        var tables = ['firma', 'title', 'tableTraffic']
        var fileName = 'Rekapitulacija po kupcima.xlsx'

        saveExcel(tables, fileName)

        this.viewType = 'pdf'
        this.hideLoader()
      }, 1000)
    },
    async getPointsOfSale () {
      const docRef = df.doc(`user_locations/${auth.currentUser.uid}.${state.getCurrentCompany().id}`)
      docRef.onSnapshot(doc => {
        var locations = Object.values(doc.data().locations)
        locations.forEach((location, index) => {
          this.locations.push(location)
        })
        // Sort locations alphabetically
        this.locations.sort((a, b) => a.name.localeCompare(b.name))
      })
    }
  }
}
</script>

<style scoped>
/* Report Wrapper - Centered */
.report-wrapper {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

/* Table Header with Filters - Sticky */
.table-header-filters {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #ffffff;
  padding: 16px;
  margin-bottom: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
}

.table-header-filters >>> .v-row {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.table-header-filters >>> .v-row:first-child {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

/* Filter Select Inputs */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}

.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}

.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}

.search-btn {
  height: 36px !important;
  padding: 0 14px !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}

.search-btn:hover:not(:disabled) {
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
}

.search-btn:disabled {
  opacity: 0.45 !important;
}

.export-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(0, 179, 190, 0.06) !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  transition: all 0.2s ease !important;
}

.export-btn:hover {
  background: rgba(0, 179, 190, 0.12) !important;
  box-shadow: 0 0 0 3px rgba(0, 179, 190, 0.12) !important;
}

/* Report Content */
.report-content-wrapper {
  margin-top: 40px;
}

.report-tables {
  width: 100%;
  max-width: 1200px;
  font-size: 16px;
  font-family: 'Roboto-Regular';
  margin: 0 auto;
}

@media (max-width: 1200px) {
  .report-tables {
    max-width: 100%;
    overflow-x: auto;
  }
}

/* Report Title */
.report-title-wrapper {
  margin-bottom: 20px;
}

.report-main-title {
  font-weight: 700;
  font-size: 24px;
  color: #2d3748;
  -webkit-text-fill-color: #2d3748;
  letter-spacing: 0.3px;
  display: inline-block;
  margin: 0;
  padding: 10px 0;
}

.report-subtitle {
  font-weight: 600;
  font-size: 14px;
  color: #666;
  margin: 0;
  padding: 5px 0;
}

/* Glass Table Wrapper */
.glass-table-wrapper {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 16px;
  overflow: hidden;
}

.glass-table-wrapper table { border-collapse: collapse; width: 100%; border: none; }
.glass-table-wrapper table th, .glass-table-wrapper table td { padding: 14px 20px; border: none; border-bottom: 1px solid #EDF2F7; font-variant-numeric: tabular-nums; }
.glass-table-wrapper table tbody tr:hover:not(.naslov):not(.row-total) { background: #F7FAFC; }

.naslov { background: #F8FAFC !important; font-weight: 700 !important; font-size: 13px !important; text-transform: uppercase !important; letter-spacing: 1px !important; color: #2D3748 !important; }
.naslov th, .naslov td { color: #2D3748 !important; font-weight: 700 !important; }
.row-total { background: rgba(0, 188, 212, 0.03) !important; font-weight: 700 !important; border-top: 2px solid rgba(0, 188, 212, 0.2) !important; color: #00838F !important; }
.row-total td, .row-total th { color: #00838F !important; font-weight: 700 !important; border-bottom: 2px solid rgba(0, 188, 212, 0.2) !important; }

.total-row {
  text-align: left;
  font-weight: 700;
  background: rgba(0, 188, 212, 0.03) !important;
  border-top: 2px solid rgba(0, 188, 212, 0.2) !important;
}

.total-row td, .total-row th {
  color: #00b3be !important;
  border-bottom: 1px solid rgba(0, 188, 212, 0.15) !important;
}

.grand-total {
  background: rgba(0, 188, 212, 0.06) !important;
  font-weight: 700;
  text-align: left;
  border-top: 2px solid rgba(0, 188, 212, 0.3) !important;
}

.grand-total td, .grand-total th {
  color: #00b3be !important;
  font-size: 15px;
  border-bottom: 2px solid rgba(0, 188, 212, 0.2) !important;
}
</style>
